/**
 * System: Gegner — Bewegung, Angriffe, Merkmalswirkungen.
 *
 * Zuständigkeit: alles, was Gegner von sich aus tun. Schaden geht durch
 * `kampf.ts`, damit es genau eine Schadenspipeline gibt.
 */

import { TAKT_DAUER } from '@pg/protocol';
import type { GegnerWesen, Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { bewegeMitKollision } from './spieler.js';
import { schadenAmSpieler } from './kampf.js';
import { abstand, abstandQ, laenge, normiere, richtungVon, vek, winkelVon } from '../vektor.js';
import { tempo } from '../tempo.js';

/** Welche Schadensart ein Gegner austeilt. Steht an der Art, nicht verstreut im Code. */
function schadensartVon(gegnerId: string): 'wucht' | 'glut' {
  return gegnerId === 'glutmotte' || gegnerId === 'der-schmelzer' ? 'glut' : 'wucht';
}

/**
 * Weckt Gegner, die nah genug sind — und lässt sie wieder ruhen, wenn der
 * Spieler weit genug weg ist.
 *
 * Ein geweckter Anführer **ruft seine Gruppe**: Das macht aus einem Champion
 * mit Gefolge eine echte Begegnung statt eines Einzelkampfs mit Zuschauern.
 */
function wecken(welt: Welt, ctx: Ctx): void {
  const a = ctx.inhalte.regelwerk.gegner.aggro;
  const s = welt.spieler;

  for (const g of welt.gegner) {
    if (g.tot) continue;
    const d = abstand(g.pos, s.pos);
    const radius = g.rang === 'gewoehnlich' ? a.wecken : a.weckenAnfuehrer;

    if (!g.wach && d <= radius) {
      g.wach = true;
      // Weckruf: Nachbarn in kurzer Reichweite kommen mit.
      if (g.rang !== 'gewoehnlich') {
        for (const nachbar of welt.gegner) {
          if (nachbar.tot || nachbar.wach) continue;
          if (abstand(nachbar.pos, g.pos) <= a.weckRuf) nachbar.wach = true;
        }
      }
    } else if (g.wach && d > a.vergessen) {
      g.wach = false;
      g.angekuendigt = false;
      g.telegraf = 0;
    }
  }
}

export function gegnerBewegung(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  const gangart = tempo(welt, ctx.inhalte).gegnerTempo;
  wecken(welt, ctx);

  for (const g of welt.gegner) {
    if (g.tot) continue;
    // Ruhende Gegner tun nichts. Das ist der Unterschied zwischen einem Raum
    // voller Begegnungen und einer Karte, die geschlossen auf einen zuläuft.
    if (!g.wach) continue;

    if (g.bereitIn > 0) g.bereitIn -= TAKT_DAUER;
    if (g.telegraf > 0) g.telegraf -= TAKT_DAUER;
    if (g.wirktNoch > 0) g.wirktNoch -= TAKT_DAUER;

    const d = abstand(g.pos, s.pos);
    const zumSpieler = richtungVon(g.pos, s.pos);
    let richtung = vek(0, 0);

    // Während eines Telegrafen steht der Gegner — das macht ihn lesbar.
    if (g.telegraf > 0) {
      richtung = vek(0, 0);
    } else {
      switch (g.def.verhalten) {
        case 'stuermer':
        case 'ausklang':
          if (d > g.def.angriffsreichweite * 0.8) richtung = zumSpieler;
          break;

        case 'umkreiser': {
          const wunsch = g.def.angriffsreichweite * 1.6;
          const seitlich = vek(-zumSpieler.y * g.umlauf, zumSpieler.x * g.umlauf);
          if (d > wunsch + 0.5) {
            richtung = normiere(
              vek(zumSpieler.x + seitlich.x * 0.6, zumSpieler.y + seitlich.y * 0.6),
            );
          } else if (d < wunsch - 0.5) {
            richtung = normiere(vek(-zumSpieler.x + seitlich.x, -zumSpieler.y + seitlich.y));
          } else {
            richtung = seitlich;
          }
          break;
        }

        case 'schuetze': {
          const wunsch = g.def.angriffsreichweite * 0.7;
          if (d > wunsch) richtung = zumSpieler;
          else if (d < wunsch * 0.55) richtung = vek(-zumSpieler.x, -zumSpieler.y);
          break;
        }
      }
    }

    if (laenge(richtung) > 0.01) g.winkel = winkelVon(richtung);

    // Gegner drücken sich voneinander weg — sonst stapeln sie sich auf einem
    // Punkt und sehen aus wie ein einziger Gegner.
    let ausweichX = 0;
    let ausweichY = 0;
    for (const anderer of welt.gegner) {
      if (anderer === g || anderer.tot) continue;
      const dq = abstandQ(g.pos, anderer.pos);
      const min = g.radius + anderer.radius;
      if (dq > min * min || dq < 1e-6) continue;
      const dist = Math.sqrt(dq);
      ausweichX += ((g.pos.x - anderer.pos.x) / dist) * (min - dist) * 2.2;
      ausweichY += ((g.pos.y - anderer.pos.y) / dist) * (min - dist) * 2.2;
    }

    bewegeMitKollision(
      welt,
      g.pos,
      vek(
        (richtung.x * g.tempo * gangart + ausweichX + g.stoss.x) * TAKT_DAUER,
        (richtung.y * g.tempo * gangart + ausweichY + g.stoss.y) * TAKT_DAUER,
      ),
      g.radius,
    );

    g.stoss.x *= 0.8;
    g.stoss.y *= 0.8;
    if (Math.abs(g.stoss.x) < 0.02) g.stoss.x = 0;
    if (Math.abs(g.stoss.y) < 0.02) g.stoss.y = 0;
  }

  // Genau ein Zug pro Takt, unabhängig von der Gegnerzahl — sonst verschiebt
  // jedes tote Wesen den Strom.
  ctx.zufall.strom('kampf').zahl();
}

function schiesse(welt: Welt, ctx: Ctx, g: GegnerWesen): void {
  const def = ctx.inhalte.geschosse.get(g.def.geschoss ?? '');
  if (!def) return;
  const richtung = richtungVon(g.pos, welt.spieler.pos);
  welt.geschosse.push({
    id: welt.naechsteId++,
    bauplan: def.id,
    pos: vek(g.pos.x + richtung.x * 0.6, g.pos.y + richtung.y * 0.6),
    gesch: vek(richtung.x * def.tempo, richtung.y * def.tempo),
    radius: def.radius,
    schaden: g.schaden,
    verbleibend: def.lebensdauer,
    vomSpieler: false,
    tot: false,
  });
}

export function gegnerAngriff(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  if (s.tot) return;
  const takt = tempo(welt, ctx.inhalte).angriffstakt;

  for (const g of welt.gegner) {
    if (g.tot || !g.wach) continue;

    // Ein angekündigter Angriff läuft ab. Der Telegraf wird jeden Takt neu
    // gemeldet, damit die Warnung dem Gegner folgt und exakt beim Zuschlagen
    // erlischt — ein einmalig gesendeter Ring bleibt als Geisterkreis stehen.
    if (g.angekuendigt) {
      if (g.telegraf > 0) {
        ctx.effekte.push({
          art: 'telegraf',
          pos: { ...g.pos },
          wert: g.def.flaechenangriff ?? g.def.angriffsreichweite,
          takt: welt.takt,
        });
        continue;
      }

      g.angekuendigt = false;
      g.bereitIn = g.def.angriffstakt * takt;
      g.wirktNoch = 0.25;

      // Er trifft NUR, wenn der Spieler noch da steht — Bewegung ist bei uns
      // das Ausweichen (Dossier §2.1).
      const reichweite = g.def.flaechenangriff ?? g.def.angriffsreichweite;
      if (abstand(g.pos, s.pos) <= reichweite + s.radius) {
        schadenAmSpieler(welt, ctx, g.schaden, schadensartVon(g.def.id));
      }
      continue;
    }

    if (g.wirktNoch > 0) continue;
    if (g.bereitIn > 0) continue;
    if (abstand(g.pos, s.pos) > g.def.angriffsreichweite + s.radius) continue;

    if (g.def.verhalten === 'schuetze' && g.def.geschoss) {
      g.bereitIn = g.def.angriffstakt * takt;
      g.wirktNoch = 0.25;
      schiesse(welt, ctx, g);
      continue;
    }

    // Große Angriffe kündigen sich an (Kanon §12.4: >= 700 ms Vorlauf).
    if (g.def.telegraf > 0) {
      g.angekuendigt = true;
      g.telegraf = g.def.telegraf;
      continue;
    }

    g.bereitIn = g.def.angriffstakt * takt;
    g.wirktNoch = 0.25;
    schadenAmSpieler(welt, ctx, g.schaden, schadensartVon(g.def.id));
  }

  ctx.zufall.strom('kampf').zahl();
}

/**
 * System: Merkmale.
 *
 * Zuständigkeit: laufende Wirkungen der Gegnermerkmale. Neue Merkmale kommen
 * als Datensatz ins Regelwerk und brauchen hier höchstens einen `case` —
 * nirgends sonst im Code.
 */
export function merkmale(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  if (s.tot) return;

  for (const g of welt.gegner) {
    if (g.tot || !g.wach || g.merkmale.length === 0) continue;

    for (const m of g.merkmale) {
      switch (m.art) {
        case 'aura': {
          // Brennt, wer zu nah steht. Tickt sekundenweise, nicht je Takt.
          g.auraTakt -= TAKT_DAUER;
          if (g.auraTakt > 0) break;
          g.auraTakt = 1;
          if (abstand(g.pos, s.pos) <= (m.radius ?? 2) + s.radius) {
            schadenAmSpieler(welt, ctx, m.schaden ?? 3, 'glut');
          }
          break;
        }
        case 'entzug': {
          // Heilt sich an dem, was er austeilt — nur bei tatsächlicher Nähe.
          if (g.leben >= g.lebenMax) break;
          if (abstand(g.pos, s.pos) > g.def.angriffsreichweite + 0.5) break;
          if (welt.takt % 30 !== 0) break;
          g.leben = Math.min(g.lebenMax, g.leben + g.lebenMax * (m.anteil ?? 0.1) * 0.05);
          break;
        }
        // 'leben', 'tempo' und 'minderung' wirken beim Bauen bzw. im Treffer.
        default:
          break;
      }
    }
  }
}
